/**
 * Premium "Partner Hero Reveal" — cineastischer Logo-Reveal + Angebot-Chips.
 *
 * Bühnenreife Partner-Enthüllung: das Logo löst sich von unscharf zu scharf
 * (Light-Sweep + Ring-Puls), Name und Kategorie erscheinen, darunter gleitet
 * eine Reihe Angebot-Chips (Titel + Preis) ein. Abschluss: Allyver-URL-Footer.
 *
 * „Link rein → fertig": Logo, Name, Stadt, Kategorie und die Angebot-Chips
 * werden über den Inspector-Loader automatisch aus dem Händler-Link befüllt
 * und sind danach frei austauschbar.
 *
 * Inputs:
 *   text     – Partner-Name
 *   subtext  – Stadt / Tagline (Footer)
 *   url      – CTA-/Shop-URL (Default: allyver.com)
 *   media[0] – Partner-Logo
 *   items    – Angebote im Format `Titel | Beschreibung | Preis | BildURL` (max 4)
 *   extras.category / extras.kategorieType – Kategorie-Pill
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  pickMediaSrc, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

function parseChip(raw: string): { title: string; price?: string } {
  const parts = String(raw ?? "").split("|").map((s) => s.trim());
  return { title: parts[0] ?? "", price: parts[2] || undefined };
}

export const partnerHeroReveal: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = inputs.accent ? accentFor(inputs) : themeColors.primary;
  const isVertical = ctx.format === "9:16";
  const name = (inputs.text ?? "PARTNER").trim();
  const footer = (inputs.subtext ?? "Jetzt live auf Allyver").trim();
  const url = (inputs.url ?? "allyver.com").trim().replace(/^https?:\/\//, "").replace(/\/$/, "");
  const kategorie = String(inputs.extras?.kategorieType ?? inputs.extras?.category ?? "Partner").trim();
  const logoUrl = pickMediaSrc(inputs.media, 0);

  const chips = (inputs.items?.length
    ? inputs.items
    : [
        "Signature-Angebot | | 12,90 € | ",
        "Bestseller | | 8,50 € | ",
        "Neu | | 19,00 € | ",
      ]
  ).slice(0, 4).map(parseChip).filter((c) => c.title);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const logoArea = isVertical ? 320 : 280;
  const nameSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.6;
  const catSize = sm * 0.8;
  const footerSize = sm * 0.82;
  const chipPx = isVertical ? md * 0.5 : md * 0.44;
  const chipPricePx = isVertical ? md * 0.5 : md * 0.46;

  const t = makeTl(clipStartSec);
  const logoSlotId = `${clipPrefix}-logo-slot`;
  const logoBlurId = `${clipPrefix}-logo-blur`;
  const logoSharpId = `${clipPrefix}-logo-sharp`;
  const ringId = `${clipPrefix}-ring`;
  const sweepBarId = `${clipPrefix}-sweep-bar`;
  const nameId = `${clipPrefix}-name`;
  const catId = `${clipPrefix}-cat`;
  const chipsId = `${clipPrefix}-chips`;
  const chipIds = chips.map((_, i) => `${clipPrefix}-chip-${i}`);
  const footerId = `${clipPrefix}-foot`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const sharedImg = "position:absolute;inset:0;margin:auto;max-width:88%;max-height:88%;width:auto;height:auto;object-fit:contain;display:block;";
  const logoBlurHtml = logoUrl
    ? `<img id="${logoBlurId}" src="${escapeHtml(logoUrl)}" alt="logo-blur" style="${sharedImg}filter:blur(${isVertical ? 22 : 18}px) saturate(0.85);opacity:0.9;" />`
    : `<div id="${logoBlurId}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${logoArea * 0.5}px;color:${themeColors.fg};filter:blur(${isVertical ? 22 : 18}px);opacity:0.6;">${escapeHtml(name.charAt(0))}</div>`;
  const logoSharpHtml = logoUrl
    ? `<img id="${logoSharpId}" src="${escapeHtml(logoUrl)}" alt="logo" style="${sharedImg}filter:drop-shadow(0 14px 36px ${withAlpha(accent, 0.45)});opacity:0;" />`
    : `<div id="${logoSharpId}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${logoArea * 0.5}px;color:${themeColors.fg};opacity:0;">${escapeHtml(name.charAt(0))}</div>`;

  const chipsHtml = chips.map((c, i) => `<div id="${chipIds[i]}" style="display:inline-flex;align-items:center;gap:12px;padding:${isVertical ? 12 : 10}px ${isVertical ? 22 : 20}px;border-radius:9999px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1px solid ${withAlpha(accent, 0.4)};box-shadow:0 12px 30px rgba(0,0,0,0.3);">
    <span style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${chipPx}px;color:${themeColors.fg};letter-spacing:-0.3px;">${escapeHtml(c.title)}</span>
    ${c.price ? `<span style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${chipPricePx}px;letter-spacing:-0.5px;color:${accent};">${escapeHtml(c.price)}</span>` : ""}
  </div>`).join("");

  const padTopBot = isVertical ? 100 : 64;

  const html = `<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 28 : 24}px;padding:${padTopBot}px 70px;">
    <div id="${logoSlotId}" style="position:relative;width:${logoArea}px;height:${logoArea}px;border-radius:32px;display:flex;align-items:center;justify-content:center;overflow:hidden;">
      ${logoBlurHtml}
      ${logoSharpHtml}
      <div id="${ringId}" style="position:absolute;inset:0;margin:auto;width:${logoArea * 0.8}px;height:${logoArea * 0.8}px;border-radius:9999px;border:2px solid ${withAlpha(accent, 0.85)};box-shadow:0 0 24px ${withAlpha(accent, 0.55)};opacity:0;pointer-events:none;"></div>
      <div style="position:absolute;inset:0;overflow:hidden;border-radius:32px;pointer-events:none;"><div id="${sweepBarId}" style="position:absolute;top:-30%;left:-60%;width:55%;height:160%;transform:rotate(18deg);background:linear-gradient(90deg, transparent 0%, ${withAlpha(themeColors.fg, 0.25)} 45%, ${withAlpha(themeColors.fg, 0.55)} 50%, ${withAlpha(themeColors.fg, 0.25)} 55%, transparent 100%);filter:blur(2px);opacity:0;"></div></div>
    </div>
    <div id="${nameId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize}px;line-height:1;letter-spacing:-2.5px;color:${themeColors.fg};text-align:center;text-shadow:0 0 30px ${withAlpha(accent, 0.3)};text-wrap:balance;">${escapeHtml(name)}</div>
    <div id="${catId}" style="display:inline-flex;align-items:center;gap:12px;padding:10px 24px;border-radius:9999px;background:${withAlpha(accent, 0.2)};border:1px solid ${withAlpha(accent, 0.55)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${catSize}px;letter-spacing:2.8px;text-transform:uppercase;color:${accent};"><span style="opacity:0.6;">Kategorie</span><span style="opacity:0.4;">·</span><span>${escapeHtml(kategorie)}</span></div>
    ${chips.length ? `<div id="${chipsId}" style="display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:${isVertical ? 12 : 14}px;max-width:${isVertical ? 900 : 1120}px;margin-top:4px;">${chipsHtml}</div>` : ""}
    <div id="${footerId}" style="display:inline-flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${footerSize}px;letter-spacing:4px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.9)};margin-top:6px;"><span style="width:26px;height:2px;background:${accent};"></span><span>${escapeHtml(footer)}</span><span style="opacity:0.5;">·</span><span style="color:${accent};">${escapeHtml(url)}</span><span style="width:26px;height:2px;background:${accent};"></span></div>
  </div>`;

  const init = [
    gsapSetLine(`#${logoSlotId}`, { opacity: 0, scale: 0.94 }),
    gsapSetLine(`#${logoBlurId}`, { opacity: 0.9 }),
    gsapSetLine(`#${logoSharpId}`, { opacity: 0, scale: 1.05 }),
    gsapSetLine(`#${ringId}`, { opacity: 0, scale: 0.55 }),
    gsapSetLine(`#${sweepBarId}`, { opacity: 0, x: -200 }),
    gsapSetLine(`#${nameId}`, { opacity: 0, y: 18 }),
    gsapSetLine(`#${catId}`, { opacity: 0, y: 14, scale: 0.9 }),
    ...chipIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 18, scale: 0.92 })),
    gsapSetLine(`#${footerId}`, { opacity: 0, y: 12 }),
  ].filter(Boolean).join("\n");

  const revealAt = 1.0;
  const tweens: string[] = [
    t.to(`#${logoSlotId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "back.out(1.4)" }, 0.1),
    // Reveal-Beat: Ring pulst, Sweep wandert, Logo blurry → sharp.
    t.fromTo(`#${ringId}`, { opacity: 0.95, scale: 0.55 }, { opacity: 0, scale: 1.7, duration: 0.85, ease: "power2.out" }, revealAt - 0.05),
    t.to(`#${sweepBarId}`, { opacity: 1, duration: 0.05, ease: "none" }, revealAt - 0.05),
    t.to(`#${sweepBarId}`, { x: 900, duration: 0.7, ease: "power2.inOut" }, revealAt - 0.05),
    t.to(`#${sweepBarId}`, { opacity: 0, duration: 0.2, ease: "power1.in" }, revealAt + 0.55),
    t.to(`#${logoBlurId}`, { opacity: 0, duration: 0.55, ease: "power2.inOut" }, revealAt + 0.05),
    t.to(`#${logoSharpId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "power2.out" }, revealAt + 0.05),
    // Name + Kategorie.
    t.to(`#${nameId}`, { opacity: 1, y: 0, duration: 0.55, ease: "back.out(1.6)" }, revealAt + 0.3),
    t.to(`#${catId}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "back.out(1.6)" }, revealAt + 0.55),
  ];

  // Chips staggern rein.
  const chipsStart = revealAt + 0.8;
  chipIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: "back.out(1.8)" }, chipsStart + i * 0.12));
  });

  const footAt = chipsStart + Math.max(chips.length, 1) * 0.12 + 0.15;
  tweens.push(t.to(`#${footerId}`, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, footAt));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
